import {
  Button,
  Card,
  Text,
  VSpacer,
  Toolbar,
} from '@/components/DesignSystem';
import { useInfiniteQuery, useMutation } from 'react-query';
import { QueryKeys } from '@/constants/QueryKeys';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { ChurnTableHeader, ChurnTableData } from '@/components/shared/CustomerChurnStatusTable';
import {
  Box,
  CircularProgress,
  Fab,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  FilterSelections,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RetailerEndpoint } from '@api/endpoints';
import { useInsightsChurnFilters } from '@/hooks/useInsightsChurnFilters';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { downloadCsv } from '@/utilities/Export';
import { ChurnStatus } from "@shared/enums";
import { useTranslation } from 'react-i18next';

interface CustomerChurnSectionProps {
  program: ApiRewardsProgram,
  retailerId: string,
}

export const InsightsChurnTableSection = ({
  program,
  retailerId,
}: CustomerChurnSectionProps) => {
  const { t } = useTranslation();

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showExportConfirmationDialog, setShowExportConfirmationDialog] = useState(false);
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => new Map());
  const { filters } = useInsightsChurnFilters(program, retailerId);
  const [sortBy, setSortBy] = useState<
    RetailerEndpoint.ListTierCustomers.TierCustomersSort
  >('churnStatus');
  const [sortDesc, setSortDesc] = useState(true);

  const churnHeaderRef = useRef<HTMLDivElement>(null);

  const stringifiedFilterSelections = JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  const queryBody: RetailerEndpoint.ListTierCustomers.Query = useMemo(() => {
    const getValues = (key: string) => Array.from(filterSelections?.get(key) ?? []);

    const churnStatus = getValues('churnStatus') as ChurnStatus[];
    const customer = getValues('customer');
    const currentStatus = getValues('current-status');
    const location = getValues('location');
    const salesperson = getValues('salesperson');

    return {
      ...(churnStatus.length && { churnStatus }),
      ...(customer.length && { id: customer }),
      ...(currentStatus.length && { rankCurrent: currentStatus.map(Number) }),
      ...(location.length && { locationId: location }),
      ...(salesperson.length && { salespersonId: salesperson }),
    };
  }, [stringifiedFilterSelections]);

  const {
    data: customers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(
    [QueryKeys.LIST_PRIMARY_CUSTOMERS, retailerId, stringifiedFilterSelections, sortBy, sortDesc],
    async ({ pageParam }) =>
      RetailerApi.listTierCustomers(retailerId, {
        limit: 20,
        page: pageParam,
        sort: sortBy,
        sortDesc: sortDesc,
        withPointsData: true,
        ...queryBody,
      }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.lastPage > lastPage.page ? lastPage.page + 1 : undefined,
    },
  );

  useEffect(() => {
    const handleScroll = () => {
      if (churnHeaderRef.current) {
        const headerTop = churnHeaderRef.current.getBoundingClientRect().top + 100;
        if (headerTop <= 0) {
          setShowScrollToTop(true);
        } else {
          setShowScrollToTop(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToChurnHeader = () => {
    if (!churnHeaderRef.current) return;

    churnHeaderRef.current.scrollIntoView({ behavior: "smooth" });

    const checkScrollComplete = () => {
      const headerTop = churnHeaderRef.current?.getBoundingClientRect().top ?? 0;
      if (Math.abs(headerTop - 100) < 2) {
        setShowScrollToTop(false);
      } else {
        requestAnimationFrame(checkScrollComplete);
      }
    };
    requestAnimationFrame(checkScrollComplete);
  };

  const handleSort = (newSortBy: RetailerEndpoint.ListTierCustomers.TierCustomersSort) => {
    const isSortCurrentlySelected = newSortBy === sortBy;
    if (isSortCurrentlySelected) {
      if (newSortBy === 'churnStatus') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy('churnStatus');
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(newSortBy);
      setSortDesc(true);
    }
  };

  const {
    mutateAsync: exportCustomers,
    isLoading: isExporting,
  } = useMutation(
    async () => {
      const csv = await RetailerApi.exportChurnCustomers(
        retailerId,
        {
          programId: program.id,
          sort: sortBy,
          sortDesc: sortDesc,
          ...queryBody,
        },
      );
      const dateString = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
      downloadCsv(csv, `customers-churn-export-${dateString}`);
    },
  );

  const LoadMoreButton = () => {
    if (isFetchingNextPage) {
      return <CircularProgress />;
    }
    return (
      <Button
        disabled={!hasNextPage || isFetchingNextPage}
        onClick={() => fetchNextPage()}
        testID="button-load-more"
      >
        {t($ => $.common.actions.loadMore)}
      </Button>
    );
  };

  const renderTableData = () => {
    if (isFetching) {
      return (
        <TableBody>
          <TableRow>
            <TableCell align="center" colSpan={12}>
              <CircularProgress />
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return <ChurnTableData
      customers={customers?.pages.flatMap(page => page.data) ?? []}
      program={program}
    />;
  };

  return (
    <Card
      cardContentStyle={{ height: '100%', padding: 0, width: '100%' }}
      sx={{ padding: '16px 20px 24px', position: 'relative' }}
      testID="churn-table-card"
    >
      <Stack alignItems="center" direction="row" gap="4px" justifyContent="space-between">
        <div ref={churnHeaderRef} >
          <Text fontSize="18px">
            {t($ => $.insights.churn.tableTitle)}
          </Text>
        </div>
        <Button
          onClick={() => setShowExportConfirmationDialog(true)}
          size="medium"
          startIcon={<ExitToAppIcon />}
          sx={{ "letter-spacing": "0.1px" }}
          testID="customer-churn-insight-export-button"
          variant="text"
        >
          {t($ => $.common.actions.export)}
        </Button>
      </Stack>
      <VSpacer size="3" />
      <Toolbar
        filters={filters}
        hideSearch
        onChange={({ selections }) => setFilterSelections(selections ?? new Map())}
        retainSelectionsOnFilterChange
        showClearAllButton
        testID="customer-churn-insight-filters"
      />
      <VSpacer size="5" />
      <Stack direction="row" justifyContent="space-between">
        <Text category="body-small" textAlign="left">
          {t($ => $.common.units.itemsCount, { count: customers?.pages?.[0]?.total ?? 0 })}
        </Text>
        <Text category="body-small" textAlign="right">
          {t($ => $.insights.export.footnote)}
        </Text>
      </Stack>
      <VSpacer size="4" />
      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <ChurnTableHeader
            handleSort={handleSort}
            sortBy={sortBy}
            sortColumns={[
              'name',
              'churnStatus',
              'rankCurrent',
              'rankEarned',
              'outstandingPointsIssued',
            ]}
            sortDesc={sortDesc}
          />
          {renderTableData()}
        </Table>
      </Box>
      <VSpacer size="8" />
      <Stack direction="row" justifyContent="center" width="100%">
        {LoadMoreButton()}
      </Stack>
      {showScrollToTop && (
        <Fab
          onClick={scrollToChurnHeader}
          size="medium"
          sx={{
            position: "fixed",
            bottom: theme => `calc(${theme.spacing(4)} + 100px)`,
            left: () => {
              const cardElement = document.querySelector('[data-testid="churn-table-card"]');
              if (cardElement) {
                const rect = cardElement.getBoundingClientRect();
                return `${rect.left}px`;
              }
              return '32px';
            },
            backgroundColor: '#393939',
            color: '#ffffff',
            boxShadow:
              '0px 14px 28px 0px rgba(0, 0, 0, 0.25), ' +
              '0px 10px 10px 0px rgba(0, 0, 0, 0.22)',
            width: '48px',
            height: '48px',
            borderRadius: '72px',
            zIndex: 2,
            '&:hover': { backgroundColor: '#555' },
          }}
        >
          <ArrowUpwardIcon />
        </Fab>
      )}
      {showExportConfirmationDialog && (
        <ConfirmDialog
          cancelText={t($ => $.common.actions.goBack)}
          confirmText={t($ => $.common.actions.export)}
          isLoading={isExporting}
          onCancel={() => setShowExportConfirmationDialog(false)}
          onConfirm={async () => {
            await exportCustomers();
            setShowExportConfirmationDialog(false);
          }}
          open
          showCloseButton={false}
          testID="export-confirmation-dialog"
        >
          {t($ => $.insights.export.confirmMessage)}
        </ConfirmDialog>
      )}
    </Card>
  );
};
